<template>
  <div class="content">
    <div class="col-md-8 ml-auto mr-auto">
      <h2 class="text-center">{{ $t('ptStops.title') }}</h2>
      <p class="text-center">
        {{ $t('ptStops.description') }}
      </p>
    </div>
    <div class="row mt-5">
      <div class="col-12">
        <card card-body-classes="table-full-width">
          <edit-pt-stop-dialog :show="showEdit" :form-details="editDetails" @close="showEdit=false" :municipalities="municipalities" :pt-zones="ptZones"/>
          <h4 slot="header" class="card-title">{{ $t('ptStops.tableTitle') }}</h4>
          <div>
            <div
              class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
            >
              <el-select
                class="select-primary mb-3 pagination-select"
                v-model="pagination.perPage"
                placeholder="Per page"
              >
                <el-option
                  class="select-primary"
                  v-for="item in pagination.perPageOptions"
                  :key="item"
                  :label="item"
                  :value="item"
                >
                </el-option>
              </el-select>

              <div class="row">
                <div class="col-6">
                  <base-input>
                    <el-input
                      type="search"
                      class="mb-3 search-input"
                      prefix-icon="el-icon-search"
                      :placeholder="$t('generic.searchInTablePlaceholder')"
                      v-model="searchQuery"
                      aria-controls="datatables"
                    >
                    </el-input>
                  </base-input>
                </div>
                <div class="col-6" v-if="$auth.can({pt_stop: 'create'}, 'permissions')">
                  <create-pt-stop-btn :pt-zones="ptZones" :municipalities="municipalities" :disabled="municipalities.length <= 0 || ptZones.length <= 0"/>
                </div>
              </div>
            </div>

            <el-table v-loading="loading" :data="queriedData" :empty-text="$t('generic.noData')">
              <el-table-column type="expand">
                <template slot-scope="props">
                  <pt-stop-details :details="props.row"/>
                </template>
              </el-table-column>
              <el-table-column
                v-for="column in tableColumns"
                :key="column.label"
                :min-width="column.minWidth"
                :prop="column.prop"
                :label="$t(column.label)"
                :formatter="column.formatter"
              >
              </el-table-column>
              <el-table-column>
                <div slot-scope="{ row }">
                  <icon-base :opacity="row.info.pavilion? 100:35" :font-size="25">
                    <span class="mdi mdi_icon mdi-bus-stop-covered"/>
                  </icon-base>
                  <icon-base :opacity="row.info.voice_tag? 100:35" :font-size="25">
                    <span class="mdi mdi_icon mdi-ear-hearing"/>
                  </icon-base>
                  <icon-base :opacity="row.info.digital_display? 100:35" :font-size="25">
                    <span class="mdi mdi_icon mdi-view-dashboard-variant-outline"/>
                  </icon-base>
                  <icon-base :opacity="row.info.paper_schedule? 100:35" :font-size="25">
                    <span class="mdi mdi_icon mdi-list-box-outline"/>
                  </icon-base>
                </div>
              </el-table-column>
              <el-table-column
                v-if="$auth.can({pt_stops: 'update'}, 'permissions') || $auth.can({pt_stops: 'delete'}, 'permissions')"
                :min-width="60" align="right">
                <div slot-scope="props">
                  <base-button
                    :disabled="municipalities.length <= 0 || ptZones.length <= 0 || ptStopUpdating"
                    v-if="$auth.can({pt_stops: 'update'}, 'permissions')"
                    @click.native="handleEdit(props.$index, props.row)"
                    class="mr-2"
                    type="info"
                    size="sm"
                    icon
                    round
                    simple>
                    <i class="tim-icons icon-pencil"></i>
                  </base-button>
                  <base-button
                    :disabled="ptStopUpdating"
                    v-if="$auth.can({pt_stops: 'delete'}, 'permissions')"
                    @click.native="handleDelete(props.$index, props.row)"
                    class=""
                    type="danger"
                    size="sm"
                    icon
                    round
                    simple>
                    <i class="tim-icons icon-simple-remove"></i>
                  </base-button>
                </div>
              </el-table-column>
            </el-table>
          </div>
          <div
            slot="footer"
            class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
          >
            <div class="">
              <p class="card-category">
                {{ $t('generic.tablePaginationSummary', {from: from + 1, to: to, total: total}) }}
              </p>
            </div>
            <base-pagination
              class="pagination-no-border"
              v-model="pagination.currentPage"
              :per-page="pagination.perPage"
              :total="total"
            >
            </base-pagination>
          </div>
        </card>
      </div>
    </div>
  </div
  >
</template>
<script>
import {Option, Select, Table, TableColumn} from 'element-ui';
import {BaseAlert, BasePagination} from 'src/components';
import Fuse from 'fuse.js';
import {mapActions, mapState} from "vuex";
import PtStopDetails from './PtStopDetails.vue'
import IconBase from 'src/components/Icon/IconBase.vue';
import CreatePtStopBtn from "./CreatePtStopBtn.vue";
import EditPtStopDialog from "./EditPtStopDialog.vue";
import swal from "sweetalert2";

export default {
  name: 'pt-stops',
  components: {
    EditPtStopDialog,
    CreatePtStopBtn,
    BasePagination,
    BaseAlert,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    PtStopDetails,
    IconBase
  },
  computed: {
    ...mapState({
      loading: state => state.pt_stops.loading,
      updating: state => state.pt_stops.updating,
      ptStopUpdating: state => state.pt_stop.updating,
      data: state => state.pt_stops.data,
      ptZones: state => state.pt_zones.data,
      municipalities: state => state.municipalities.data,
    }),
    /***
     * Returns a page from the searched data or the whole data. Search is performed in the watch section below
     */
    queriedData() {
      let result = this.tableData;
      if (this.searchedData.length > 0) {
        result = this.searchedData;
      }
      return result.slice(this.from, this.to);
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    total() {
      return this.searchedData.length > 0
        ? this.searchedData.length
        : this.tableData.length;
    }
  },
  data() {
    return {
      pagination: {
        perPage: 50,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50],
        total: 0
      },
      searchQuery: '',
      propsToSearch: ['stop_id', 'name', 'zone_id'],
      tableColumns: [
        {
          prop: 'stop_id',
          label: 'ptStops.details.stop_id',
        },
        {
          prop: 'name',
          label: 'ptStops.details.name',
        },
        {
          prop: 'zone_id',
          label: 'ptStops.details.zone_id',
        },
        {
          prop: 'municipality_id',
          label: 'ptStops.details.municipality_id',
        },
      ],
      tableData: [],
      searchedData: [],
      fuseSearch: null,
      showEdit: false,
      editDetails: {
        info: {}
      }
    };
  },
  methods: {
    ...mapActions(['GetPTStops', 'DeletePtStop', 'GetPTZones', 'LoadMunicipalities']),
    handleEdit(index, row) {
      this.editDetails = {
        id: row.id,
        stop_id: row.stop_id,
        name: row.name,
        zone_id: row.zone_id,
        municipality_id: row.municipality_id,
        info: {
          pavilion: row.info.pavilion,
          voice_tag: row.info.voice_tag,
          digital_display: row.info.digital_display,
          paper_schedule: row.info.paper_schedule,
          metadataKeyVal: JSON.stringify(row.info.metadata)
        },
      };
      this.showEdit = true;
    },
    handleDelete(index, row) {
      swal({
        title: this.$t('ptStops.delete.text'),
        text: `'${row.stop_id}'`,
        confirmButtonText: this.$t('ptStops.delete.actionBtnLabel'),
        cancelButtonText: this.$t('ptStops.delete.cancelBtnLabel'),
        buttonsStyling: false,
        confirmButtonClass: 'btn btn-danger',
        cancelButtonClass: 'btn btn-secondary',
        type: 'question',
        showCancelButton: true
      }).then(result => {
        if (result.value) {
          this.DeletePtStop(row).then(() => {
            if (this.error == null) {
              this.$notify({
                type: 'success',
                message: this.$t('ptStops.delete.deletedNotification', row),
                icon: 'tim-icons icon-alert-circle-exc'
              })
            }

          })
        }
      });
    },
  },
  mounted() {
    // Loading pt-stops data
    this.GetPTStops()

    if (this.$auth.can({pt_zones: 'read'}, 'permissions') && this.ptZones.length <= 0) {
      // Load if not loaded.
      this.GetPTZones();
    }

    if (this.$auth.can({municipalities: 'read'}, 'permissions') && this.municipalities.length <= 0) {
      // Load if not loaded.
      this.LoadMunicipalities();
    }
  },
  watch: {
    /**
     * Searches through the table data by a given query.
     * NOTE: If you have a lot of data, it's recommended to do the search on the Server Side and only display the results here.
     * @param value of the query
     */
    searchQuery(value) {
      let result = this.tableData;
      if (value !== '') {
        result = this.fuseSearch.search(this.searchQuery);
      }
      this.searchedData = result;
    },
    "data"(data) {
      this.tableData = data
      // Fuse search initialization.
      this.fuseSearch = new Fuse(this.tableData, {
        keys: this.propsToSearch,
        threshold: 0.3
      });

      if (this.searchQuery !== '') {
        this.searchedData = this.fuseSearch.search(this.searchQuery);
      }
    },
  }
};
</script>
<style>
.pagination-select,
.search-input {
  width: 200px;
}
</style>
